import { useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  attributesForCategory,
  resolvedCategory,
  typeIsOther,
  typesForCategory,
  usePropertyTaxonomy,
  withAttribute,
  withCategory,
  withLandField,
  withOtherLabel,
  withSubtype,
  withType,
  type AttributeValue,
  type TaxonomyAttribute,
  type TaxonomyAttributeChild,
  type TaxonomyFormSlice,
} from '@/lib/propertyTaxonomy'

export interface PropertyTaxonomyFieldsProps {
  selection: TaxonomyFormSlice
  onChange: (next: TaxonomyFormSlice) => void
  idPrefix?: string
  appearance?: 'composer' | 'plain'
  typeError?: string
}

export function PropertyTaxonomyFields({
  selection,
  onChange,
  idPrefix = 'taxonomy',
  appearance = 'composer',
  typeError,
}: PropertyTaxonomyFieldsProps) {
  const { taxonomy, status } = usePropertyTaxonomy()
  const category = resolvedCategory(selection, taxonomy)
  const types = typesForCategory(taxonomy, category)
  const selectedType = types.find((t) => t.code === selection.property_type)
    ?? taxonomy.types.find((t) => t.code === selection.property_type)
  const subtypes = selectedType?.subtypes ?? []
  const showOther = typeIsOther(selectedType)
  const showLand = category === 'land'
  const groups = attributesForCategory(taxonomy, category)

  useEffect(() => {
    if (selection.category || !taxonomy.types.length || !selection.property_type) return
    const derived = taxonomy.types.find((t) => t.code === selection.property_type)?.category_code
    if (!derived) return
    onChange({ ...selection, category: derived })
  }, [taxonomy, selection, onChange])

  const fieldId = (name: string) => `${idPrefix}-${name}`
  const selectClass =
    appearance === 'composer'
      ? cn(
          'mt-1 flex h-10 w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)]',
          'bg-[var(--lc-surface-raised)] px-3 text-sm text-[var(--lc-text-primary)]',
        )
      : 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

  return (
    <div className="space-y-4">
      {status === 'error' ? (
        <p role="status" className="text-sm text-[var(--lc-text-muted)]">
          Property types are unavailable.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={fieldId('category')}>Category</Label>
          <select
            id={fieldId('category')}
            className={selectClass}
            value={category}
            disabled={status === 'loading'}
            onChange={(e) => onChange(withCategory(selection, taxonomy, e.target.value))}
          >
            {status === 'loading' ? <option value="">Loading…</option> : null}
            {category === '' ? <option value="">Select category</option> : null}
            {taxonomy.categories.map((cat) => (
              <option key={cat.code} value={cat.code}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={fieldId('type')}>
            Property type <span className="text-[var(--lc-status-danger-fg)]">*</span>
          </Label>
          <select
            id={fieldId('type')}
            className={selectClass}
            value={selection.property_type}
            disabled={status === 'loading' || !category}
            onChange={(e) => onChange(withType(selection, taxonomy, e.target.value))}
          >
            <option value="">Select type</option>
            {types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </select>
          {typeError ? (
            <p className="mt-1 text-[length:var(--lc-type-caption)] text-[var(--lc-status-danger-fg)]">
              {typeError}
            </p>
          ) : null}
        </div>
      </div>

      {subtypes.length > 0 ? (
        <div>
          <Label htmlFor={fieldId('subtype')}>Subtype</Label>
          <select
            id={fieldId('subtype')}
            className={selectClass}
            value={selection.property_subtype}
            onChange={(e) => onChange(withSubtype(selection, e.target.value))}
          >
            <option value="">None</option>
            {subtypes.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {showOther ? (
        <div>
          <Label htmlFor={fieldId('other')}>Describe this type</Label>
          <Input
            id={fieldId('other')}
            className="mt-1"
            value={selection.other_label}
            placeholder="What should we call it?"
            onChange={(e) => onChange(withOtherLabel(selection, e.target.value))}
          />
          <p className="mt-1 text-xs text-[var(--lc-text-muted)]">
            The listing is stored as Other. This description is sent as a suggestion for review.
          </p>
        </div>
      ) : null}

      {showLand ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Land</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor={fieldId('plot-area')}>Plot area</Label>
              <Input
                id={fieldId('plot-area')}
                className="mt-1"
                inputMode="decimal"
                value={selection.plot_area}
                onChange={(e) => onChange(withLandField(selection, 'plot_area', e.target.value.replace(/[^\d.]/g, '')))}
              />
            </div>
            <div>
              <Label htmlFor={fieldId('bua')}>Permissible build-up area</Label>
              <Input
                id={fieldId('bua')}
                className="mt-1"
                inputMode="decimal"
                value={selection.permissible_buildup_area}
                onChange={(e) =>
                  onChange(withLandField(selection, 'permissible_buildup_area', e.target.value.replace(/[^\d.]/g, '')))
                }
              />
              <p id={fieldId('bua-help')} className="mt-1 text-xs text-[var(--lc-text-muted)]">
                Total construction allowed. Not the same as plot area.
              </p>
            </div>
            <div>
              <Label htmlFor={fieldId('coverage')}>Site coverage (%)</Label>
              <Input
                id={fieldId('coverage')}
                className="mt-1"
                inputMode="decimal"
                value={selection.site_coverage_percent}
                onChange={(e) =>
                  onChange(withLandField(selection, 'site_coverage_percent', e.target.value.replace(/[^\d.]/g, '')))
                }
              />
            </div>
            <div>
              <Label htmlFor={fieldId('floors')}>Permitted floors</Label>
              <Input
                id={fieldId('floors')}
                className="mt-1"
                inputMode="numeric"
                value={selection.permitted_floors}
                onChange={(e) =>
                  onChange(withLandField(selection, 'permitted_floors', e.target.value.replace(/\D/g, '')))
                }
              />
            </div>
            <div>
              <Label htmlFor={fieldId('zone')}>Zone code</Label>
              <Input
                id={fieldId('zone')}
                className="mt-1"
                value={selection.zone_code}
                onChange={(e) => onChange(withLandField(selection, 'zone_code', e.target.value))}
              />
            </div>
          </div>
        </fieldset>
      ) : null}

      {groups.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm font-medium">Attributes</p>
          {groups.map((group) => (
            <fieldset key={group.group_code} className="space-y-2">
              <legend className="text-xs font-semibold uppercase tracking-wide text-[var(--lc-text-muted)]">
                {group.label}
              </legend>
              {group.attributes.map((attr) => (
                <AttributeControl
                  key={attr.code}
                  attr={attr}
                  idPrefix={idPrefix}
                  values={selection.attribute_values}
                  onValue={(code, value) => onChange(withAttribute(selection, taxonomy.attributes, code, value))}
                />
              ))}
            </fieldset>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function AttributeControl({
  attr,
  idPrefix,
  values,
  onValue,
}: {
  attr: TaxonomyAttribute
  idPrefix: string
  values: Record<string, AttributeValue>
  onValue: (code: string, value: AttributeValue) => void
}) {
  const checked = values[attr.code] === true
  return (
    <div className="space-y-2">
      <AttributeInput attr={attr} idPrefix={idPrefix} values={values} onValue={onValue} />
      {attr.data_type === 'boolean' && checked
        ? attr.children.map((child) => (
            <div key={child.code} className="ms-6">
              <AttributeInput attr={child} idPrefix={idPrefix} values={values} onValue={onValue} />
            </div>
          ))
        : null}
    </div>
  )
}

function AttributeInput({
  attr,
  idPrefix,
  values,
  onValue,
}: {
  attr: TaxonomyAttribute | TaxonomyAttributeChild
  idPrefix: string
  values: Record<string, AttributeValue>
  onValue: (code: string, value: AttributeValue) => void
}) {
  const id = `${idPrefix}-attr-${attr.code}`
  const current = values[attr.code]

  if (attr.data_type === 'boolean') {
    return (
      <label htmlFor={id} className="flex items-center gap-2 text-sm">
        <input
          id={id}
          type="checkbox"
          checked={current === true}
          onChange={(e) => onValue(attr.code, e.target.checked)}
        />
        {attr.label}
      </label>
    )
  }

  if (attr.data_type === 'single_choice') {
    return (
      <div>
        <Label htmlFor={id}>{attr.label}</Label>
        <select
          id={id}
          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={typeof current === 'string' ? current : ''}
          onChange={(e) => onValue(attr.code, e.target.value)}
        >
          <option value="">Select</option>
          {(attr.allowed_values || []).map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    )
  }

  if (attr.data_type === 'multi_choice') {
    const selected = Array.isArray(current) ? current : []
    return (
      <fieldset>
        <legend className="text-sm">{attr.label}</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {(attr.allowed_values || []).map((opt) => {
            const on = selected.includes(opt.code)
            return (
              <label key={opt.code} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = on ? selected.filter((c) => c !== opt.code) : [...selected, opt.code]
                    onValue(attr.code, next)
                  }}
                />
                {opt.label}
              </label>
            )
          })}
        </div>
      </fieldset>
    )
  }

  return (
    <div>
      <Label htmlFor={id}>
        {attr.label}
        {attr.unit ? ` (${attr.unit})` : ''}
      </Label>
      <Input
        id={id}
        className="mt-1"
        inputMode={attr.data_type === 'integer' ? 'numeric' : 'decimal'}
        value={current == null ? '' : String(current)}
        onChange={(e) => {
          // Never emit a non-finite value. The old form was
          // `raw === '' ? '' : Number(raw)`, and the [^\d.] filter admits a lone
          // '.' and multiple dots -- so Number('.') / Number('1.2.3') produced
          // NaN, which serialises to JSON null and makes the API reject the
          // whole listing save with an error that names no field.
          const digitsOnly = attr.data_type === 'integer'
          const cleaned = digitsOnly
            ? e.target.value.replace(/\D/g, '')
            // Keep the first decimal point, drop any later ones.
            : e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
          if (cleaned === '' || cleaned === '.') {
            onValue(attr.code, '')
            return
          }
          // Mid-typing '12.' parses to 12, which would rewrite the input and
          // strip the point the user just typed. Hold it as text until there
          // is a digit after it; serializeAttributes drops a trailing-dot
          // string so it can never reach the API.
          if (cleaned.endsWith('.')) {
            onValue(attr.code, cleaned)
            return
          }
          const parsed = Number(cleaned)
          onValue(attr.code, Number.isFinite(parsed) ? parsed : '')
        }}
      />
    </div>
  )
}

export function RegistryPropertyTypePicker({
  selected,
  onChange,
  labelledBy,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  labelledBy: string
}) {
  const { taxonomy, status } = usePropertyTaxonomy()

  if (status === 'loading') {
    return <p role="status">Loading property types…</p>
  }
  if (status === 'error' || taxonomy.types.length === 0) {
    return <p role="status">Property types are unavailable.</p>
  }

  // Values stored before the registry existed ('commercial', 'land', 'mixed'
  // were categories, not type codes) match no button, so they rendered as
  // unselected and the next save silently dropped them. Show them instead:
  // the agent can keep or clear a recorded preference deliberately.
  const knownCodes = new Set(taxonomy.types.map((t) => t.code))
  const unknownSelected = selected.filter((code) => !knownCodes.has(code))

  return (
    <div role="group" aria-labelledby={labelledBy} className="space-y-3">
      {taxonomy.categories.map((cat) => {
        const types = taxonomy.types.filter((t) => t.category_code === cat.code)
        if (!types.length) return null
        return (
          <div key={cat.code}>
            <p className="mb-1 text-xs font-medium text-[var(--lc-text-muted)]">{cat.label}</p>
            <div className="flex flex-wrap gap-2">
              {types.map((opt) => {
                const on = selected.includes(opt.code)
                return (
                  <button
                    key={opt.code}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      onChange(on ? selected.filter((t) => t !== opt.code) : [...selected, opt.code])
                    }
                    className={cn(
                      'min-h-tap rounded-[var(--lc-radius-md)] border px-3 text-sm',
                      on
                        ? 'border-[var(--lc-action-primary)] bg-[var(--lc-action-primary)] text-[var(--lc-action-primary-text)]'
                        : 'border-[var(--lc-border)] bg-[var(--lc-surface-raised)]',
                    )}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
      {unknownSelected.length > 0 ? (
        <div>
          <p className="mb-1 text-xs font-medium text-[var(--lc-text-muted)]">
            Recorded earlier, not in the type registry
          </p>
          <div className="flex flex-wrap gap-2">
            {unknownSelected.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed
                title="Recorded before the type registry. Clear it, or pick the matching type above."
                onClick={() => onChange(selected.filter((t) => t !== code))}
                className={cn(
                  'min-h-tap rounded-[var(--lc-radius-md)] border px-3 text-sm',
                  'border-[var(--lc-border-strong)] bg-[var(--lc-surface-raised)] text-[var(--lc-text-muted)]',
                )}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
